/**
 * t293u: the Tasks lists as calm tables, like Issues (t276u). Pure helpers shared by the desktop table (TasksTable),
 * the project list view and the phone rows: sorting (headers and the Sort select use the same keys, so they agree),
 * "Relates to" (where a task lives or came from), and the due / reminder text.
 */
import { DateTime } from 'luxon';
import { TODO_TZ, todoRemindAt, type Team, type TaskPriority, type TaskSummary } from '@tradeoutlet/shared';

export type TaskRowData = TaskSummary & {
  /** Tasks area rows (TaskListItem); project rows have status_name from the project's statuses. */
  project_name?: string; project_color?: string; status_name?: string; status_color?: string; status_category?: string; location_name?: string | null;
};

export type TaskSort = 'smart' | 'position' | 'title' | 'title_desc' | 'relates' | 'relates_desc' | 'due' | 'due_desc' | 'priority' | 'priority_asc' | 'status' | 'status_desc' | 'updated';
export const TASK_SORTS: { key: TaskSort; label: string }[] = [
  { key: 'smart', label: 'Due soonest, then priority' },
  { key: 'due', label: 'Due date (soonest first)' },
  { key: 'due_desc', label: 'Due date (latest first)' },
  { key: 'priority', label: 'Priority (highest first)' },
  { key: 'priority_asc', label: 'Priority (lowest first)' },
  { key: 'title', label: 'Task (A–Z)' },
  { key: 'title_desc', label: 'Task (Z–A)' },
  { key: 'relates', label: 'Relates to (A–Z)' },
  { key: 'status', label: 'Status' },
  { key: 'updated', label: 'Recently changed' },
];
export const isTaskSort = (s: string | null | undefined): s is TaskSort => Boolean(s) && [...TASK_SORTS.map(x => x.key), 'position', 'relates_desc', 'status_desc'].includes(s as TaskSort);

/** Each sortable column's two directions; the first is what a first click picks. */
export const TASK_COLUMN_SORTS = {
  title: ['title', 'title_desc'], relates: ['relates', 'relates_desc'], due: ['due', 'due_desc'], priority: ['priority', 'priority_asc'], status: ['status', 'status_desc'],
} as const satisfies Record<string, readonly [TaskSort, TaskSort]>;
export type TaskColumn = keyof typeof TASK_COLUMN_SORTS;
export function nextTaskSort(col: TaskColumn, current: TaskSort): TaskSort {
  const [a, b] = TASK_COLUMN_SORTS[col];
  return current === a ? b : a;
}
export function taskSortState(col: TaskColumn, current: TaskSort): 'ascending' | 'descending' | 'none' {
  const [a, b] = TASK_COLUMN_SORTS[col];
  if (current !== a && current !== b) return 'none';
  const desc: TaskSort[] = ['title_desc', 'relates_desc', 'due_desc', 'priority', 'status_desc'];
  return desc.includes(current) ? 'descending' : 'ascending';
}

const PRIORITY_RANK: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };
const STATUS_RANK: Record<string, number> = { backlog: 0, todo: 1, in_progress: 2, done: 3, cancelled: 4 };
const dueKey = (t: TaskRowData) => (t.due_date ? `${t.due_date}T${t.due_time ?? '99:99'}` : '9999');

export function sortTasks<T extends TaskRowData>(list: T[], by: TaskSort, relates: (t: T) => string = t => relatesSortText(t)): T[] {
  const out = [...list];
  const byDue = (a: T, b: T) => dueKey(a).localeCompare(dueKey(b));
  const byPri = (a: T, b: T) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  const done = (a: T, b: T) => Number(Boolean(a.completed_at)) - Number(Boolean(b.completed_at));
  const cmp: Record<TaskSort, (a: T, b: T) => number> = {
    smart: (a, b) => done(a, b) || byDue(a, b) || byPri(a, b),
    position: (a, b) => a.position - b.position || a.number - b.number,
    title: (a, b) => a.title.localeCompare(b.title, 'en-GB'),
    title_desc: (a, b) => b.title.localeCompare(a.title, 'en-GB'),
    relates: (a, b) => relates(a).localeCompare(relates(b), 'en-GB') || byDue(a, b),
    relates_desc: (a, b) => relates(b).localeCompare(relates(a), 'en-GB') || byDue(a, b),
    due: (a, b) => byDue(a, b) || byPri(a, b),
    // Latest first, tasks with no due date still last.
    due_desc: (a, b) => (dueKey(a) === '9999' ? 1 : 0) - (dueKey(b) === '9999' ? 1 : 0) || dueKey(b).localeCompare(dueKey(a)),
    priority: (a, b) => byPri(a, b) || byDue(a, b),
    priority_asc: (a, b) => byPri(b, a) || byDue(a, b),
    status: (a, b) => (STATUS_RANK[a.status_category ?? ''] ?? 9) - (STATUS_RANK[b.status_category ?? ''] ?? 9) || byDue(a, b),
    status_desc: (a, b) => (STATUS_RANK[b.status_category ?? ''] ?? 9) - (STATUS_RANK[a.status_category ?? ''] ?? 9) || byDue(a, b),
    updated: (a, b) => b.updated_at.localeCompare(a.updated_at),
  };
  return out.sort(cmp[by]);
}

/** One piece of "Relates to": plain muted text, a link where it makes sense. */
export interface RelatesPart { key: string; text: string; href?: string; icon?: 'lock' | 'chat' | 'dot' | 'team' | 'feedback'; color?: string; title?: string }

/** "DM with Arlo Fisher", "#general", "Ops huddle" (an ad-hoc meeting room), "Arlo, Bea" (a group chat). */
export function chatOrigin(t: Pick<TaskSummary, 'source_message_id' | 'source_conversation_id' | 'source_conversation_kind' | 'source_label'>): RelatesPart | null {
  if (!t.source_message_id) return null;
  const where = !t.source_label ? null : t.source_conversation_kind === 'dm' ? `DM with ${t.source_label}` : t.source_label;
  return {
    key: 'chat', icon: 'chat', text: where ? `From chat · ${where}` : 'From chat',
    href: t.source_conversation_id ? `/c/${t.source_conversation_id}#m-${t.source_message_id}` : undefined,
    title: t.source_conversation_id ? 'Open the message this task was made from' : 'Made from a chat message you are not in',
  };
}

export function relatesTo(t: TaskRowData, teams?: Map<string, Pick<Team, 'name' | 'kind'>>, opts: { inProject?: boolean; milestone?: string } = {}): RelatesPart[] {
  const out: RelatesPart[] = [];
  if (t.is_private) out.push({ key: 'private', icon: 'lock', text: 'Private', title: 'Private: only you can see this task' });
  else if (!opts.inProject && !t.no_project && t.project_name) out.push({ key: 'project', icon: 'dot', color: t.project_color, text: t.project_name, href: `/projects/${t.project_id}` });
  if (opts.milestone) out.push({ key: 'milestone', text: `Milestone: ${opts.milestone}` });
  if (t.feedback_kind) out.push({ key: 'feedback', icon: 'feedback', text: t.feedback_kind === 'bug' ? 'From a bug report' : 'From a suggestion' });
  const chat = chatOrigin(t);
  if (chat) out.push(chat);
  (t.team_ids ?? []).forEach((id, i) => {
    const team = teams?.get(id);
    const name = team?.name ?? t.team_names?.[i];
    if (name) out.push({ key: `team:${id}`, icon: 'team', text: name, href: `/${team?.kind === 'group' ? 'groups' : 'departments'}?team=${id}` });
  });
  if (!out.length && t.no_project && !opts.inProject) out.push({ key: 'none', text: 'No project' });
  return out;
}

/** The text "Relates to" sorts on. */
export const relatesSortText = (t: TaskRowData) => relatesTo(t).map(p => p.text).join(' ') || '~';

/** When the reminder goes off, if there is one: the task's own, or your private "Remind me about this task". */
export function taskReminderAt(t: Pick<TaskSummary, 'remind_at' | 'due_date' | 'due_time' | 'reminder_minutes' | 'my_reminder_at'>): string | null {
  return t.remind_at ?? (t.due_date ? todoRemindAt({ due_date: t.due_date, due_time: t.due_time ?? null, reminder_minutes: t.reminder_minutes ?? null }) : null) ?? t.my_reminder_at ?? null;
}
export const hasReminder = (t: Pick<TaskSummary, 'remind_at' | 'due_date' | 'due_time' | 'reminder_minutes' | 'my_reminder_at'>) => Boolean(taskReminderAt(t));

/** "Reminder Fri 23 Oct, 09:00" (UK time). */
export function reminderText(t: Pick<TaskSummary, 'remind_at' | 'due_date' | 'due_time' | 'reminder_minutes' | 'my_reminder_at'>): string | null {
  const at = taskReminderAt(t);
  if (!at) return null;
  const d = DateTime.fromISO(at, { zone: TODO_TZ });
  return d.isValid ? `Reminder ${d.toFormat('ccc d LLL, HH:mm')}` : null;
}

/** "Overdue · Fri 23 Oct", "Fri 23 Oct 14:00", "Done" dates are never red. */
export function taskDueText(t: Pick<TaskSummary, 'due_date' | 'due_time' | 'completed_at'>, today: string): { text: string; overdue: boolean } {
  if (!t.due_date) return { text: '', overdue: false };
  const day = DateTime.fromISO(t.due_date, { zone: TODO_TZ }).toFormat('ccc d LLL');
  const label = t.due_time ? `${day} ${t.due_time}` : day;
  const overdue = !t.completed_at && t.due_date < today;
  return { text: overdue ? `Overdue · ${label}` : label, overdue };
}

export const PRIORITY_LABEL: Record<TaskPriority, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low', none: '' };
export const PRIORITY_DOT: Record<TaskPriority, string> = { urgent: 'bg-rose-600', high: 'bg-orange-500', medium: 'bg-slate-400', low: 'bg-slate-300 dark:bg-slate-600', none: '' };

/** The phone's muted line: "Peak trading 2026 · Due Fri 23 Oct", "Private · 🔔", "From chat · #general · Overdue · Mon 19 Oct". */
export function phoneRelatesLine(t: TaskRowData, today: string, teams?: Map<string, Pick<Team, 'name' | 'kind'>>): string {
  const parts = relatesTo(t, teams).map(p => p.text);
  const due = taskDueText(t, today);
  if (due.text) parts.push(due.overdue ? due.text : `Due ${due.text}`);
  if (hasReminder(t)) parts.push('🔔');
  return parts.join(' · ');
}
